/**
 * Formulário de criação de requisição (RQA/RQI) — wizard mobile first.
 * Uma pergunta por passo (ver Wizard.jsx + wizardSteps.jsx); este componente
 * é o dono do ESTADO (cabeçalho + itens + fotos), do rascunho e do envio.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { criarRequisicao, listarClientesSelect } from "../../../services/solicitacoes";
import Wizard from "./Wizard";
import { passosRequisicao } from "./wizardSteps";
import { carregarRascunho, limparRascunho, useAutosaveRascunho } from "./rascunho";
import {
  erroDetalheDoMotivo, gerarUid, parseDecimal, registroIntocado, unwrapItem,
  unwrapLista,
} from "./utils";
import { BannerRascunho } from "./styles";

function novoItem() {
  return {
    uid: gerarUid(),
    codigo: "",
    descricao: "",
    // Produto do Cadastro de Suprimentos ({id, codigo, nome, unidade}) ou null.
    produto: null,
    ambiente: "",
    medida: "",
    quantidade: "",
    unidade: "UN",
    motivo: "",
    motivo_detalhe: "",
  };
}

function cabecalhoVazio() {
  return {
    num_pedido: "",
    cliente: null,
    clienteTexto: "",
    supervisor_nome: "",
    lider_montagem_nome: "",
    setor: "",
    lote_producao: "",
  };
}

export default function CriarRequisicao({ tipoInfo }) {
  const tipo = tipoInfo.value;
  const ehRQA = tipo === "RQA";
  const ehRQI = tipo === "RQI";
  const navigate = useNavigate();
  const { request } = useAuth();
  const toast = useToast();

  const rascunhoInicial = useMemo(() => carregarRascunho(tipo), [tipo]);
  const [cab, setCab] = useState(() => ({
    ...cabecalhoVazio(),
    ...(rascunhoInicial?.cab || {}),
  }));
  const [itens, setItens] = useState(() =>
    Array.isArray(rascunhoInicial?.itens) && rascunhoInicial.itens.length
      ? rascunhoInicial.itens.map((it) => ({ ...novoItem(), ...it, uid: gerarUid() }))
      : [novoItem()],
  );
  const [fotos, setFotos] = useState([]);
  const [mostrarBannerRascunho, setMostrarBannerRascunho] = useState(!!rascunhoInicial);
  const [sujo, setSujo] = useState(false);
  const [erros, setErros] = useState({});

  const [clientes, setClientes] = useState([]);
  const [clientesCarregando, setClientesCarregando] = useState(true);

  useAutosaveRascunho(tipo, { cab, itens }, sujo);

  useEffect(() => {
    let cancelado = false;
    listarClientesSelect(request)
      .then((payload) => {
        if (cancelado) return;
        setClientes(
          unwrapLista(payload).map((c) => ({
            id: c.id,
            nome: c.nome,
            label: c.nome,
            sublabel: "",
          })),
        );
      })
      .catch(() => {})
      .finally(() => !cancelado && setClientesCarregando(false));
    return () => {
      cancelado = true;
    };
  }, [request]);

  const mudarCab = (campo, valor) => {
    setSujo(true);
    setCab((atual) => ({ ...atual, [campo]: valor }));
    setErros((e) => {
      const copia = { ...e };
      delete copia[campo === "cliente" || campo === "clienteTexto" ? "cliente" : campo];
      return copia;
    });
  };

  const mudarItem = (indice, campo, valor) => {
    setSujo(true);
    setItens((atual) =>
      atual.map((it, i) => (i === indice ? { ...it, [campo]: valor } : it)),
    );
    setErros((e) => {
      const copia = { ...e };
      delete copia[`i${indice}_${campo}`];
      // Trocar o motivo pode dispensar o detalhe (saiu de "Outro").
      if (campo === "motivo") delete copia[`i${indice}_motivo_detalhe`];
      return copia;
    });
  };

  // Retorna o uid do novo item para o wizard saltar direto para ele.
  const adicionarItem = () => {
    const novo = novoItem();
    setSujo(true);
    setItens((atual) => [...atual, novo]);
    return novo.uid;
  };

  const removerItem = (indice) => {
    setSujo(true);
    setItens((atual) => (atual.length > 1 ? atual.filter((_, i) => i !== indice) : atual));
    setErros({});
  };

  // Item em branco pode ser descartado sem confirmar — é o que torna reversível
  // o clique acidental em "Adicionar outro item" (ver decorarSaidaDoBloco).
  const itemIntocado = (item) => registroIntocado(item, novoItem());

  const mudarFotos = (novas) => {
    setSujo(true);
    setFotos(novas);
  };

  const descartarRascunho = () => {
    limparRascunho(tipo);
    setCab(cabecalhoVazio());
    setItens([novoItem()]);
    setFotos([]);
    setSujo(false);
    setErros({});
    setMostrarBannerRascunho(false);
  };

  function validar() {
    const e = {};
    if (!cab.cliente) {
      e.cliente = "Selecione o cliente da lista.";
    }
    itens.forEach((it, i) => {
      if (!it.descricao.trim()) e[`i${i}_descricao`] = "Informe a descrição.";
      const qtd = parseDecimal(it.quantidade);
      if (!qtd || qtd <= 0) e[`i${i}_quantidade`] = "Informe a quantidade.";
      if (ehRQA) {
        if (!it.ambiente.trim()) e[`i${i}_ambiente`] = "Informe o ambiente.";
        if (!it.medida.trim()) e[`i${i}_medida`] = "Informe a medida.";
        if (!it.motivo) e[`i${i}_motivo`] = "Selecione o motivo.";
        const erroDetalhe = erroDetalheDoMotivo(it.motivo, it.motivo_detalhe);
        if (erroDetalhe) e[`i${i}_motivo_detalhe`] = erroDetalhe;
      }
    });
    return e;
  }

  function montarPayload() {
    return {
      tipo,
      num_pedido: cab.num_pedido.trim(),
      cliente_id: cab.cliente?.id || undefined,
      cliente_nome: cab.cliente?.nome || "",
      supervisor_nome: cab.supervisor_nome.trim(),
      lider_montagem_nome: cab.lider_montagem_nome.trim(),
      itens: itens.map((it) => ({
        produto_id: it.produto?.id || undefined,
        codigo: it.codigo.trim(),
        descricao: it.descricao.trim(),
        ambiente: ehRQA ? it.ambiente.trim() : "",
        medida: ehRQA ? it.medida.trim() : "",
        quantidade: parseDecimal(it.quantidade),
        unidade_medida: ehRQI ? it.unidade : "UN",
        motivo: ehRQA ? it.motivo : "",
        motivo_detalhe: ehRQA && it.motivo ? it.motivo_detalhe.trim() : "",
      })),
      fotos: fotos.map((f) => ({ imagem: f.imagem, legenda: f.legenda || "" })),
    };
  }

  const enviar = async () => {
    const e = validar();
    if (Object.keys(e).length) {
      setErros(e);
      toast.error("Verifique os campos destacados.");
      throw new Error("Campos inválidos.");
    }
    setErros({});
    const resposta = await criarRequisicao(request, montarPayload());
    const solicitacao = unwrapItem(resposta);
    limparRascunho(tipo);
    navigate("../comprovante", {
      state: { solicitacao, familia: "requisicao", tipo },
    });
  };

  const passos = passosRequisicao({
    tipo,
    cab,
    itens,
    fotos,
    clientes,
    clientesCarregando,
    erros,
    mudarCab,
    mudarItem,
    adicionarItem,
    removerItem,
    itemIntocado,
    mudarFotos,
  });

  const aviso = mostrarBannerRascunho ? (
    <BannerRascunho>
      <span>Rascunho recuperado — as fotos precisam ser adicionadas de novo.</span>
      <button type="button" onClick={() => setMostrarBannerRascunho(false)}>
        Continuar
      </button>
      <button type="button" onClick={descartarRascunho}>
        Descartar
      </button>
    </BannerRascunho>
  ) : null;

  return (
    <Wizard
      titulo={tipoInfo.label}
      codigo={`Nova solicitação · ${tipo}`}
      aoSair={() => navigate("..")}
      passos={passos}
      onEnviar={enviar}
      podeEnviar={Object.keys(validar()).length === 0}
      aviso={aviso}
    />
  );
}
